import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PreLoader } from "../../subcomponents/Preloader";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import { checkDuplicate, deleteItem } from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import InfoIcon from "@mui/icons-material/Info";
import AlertComponent from "../../subcomponents/AlertComponent";
import { transportationTooltip } from "../../../constants/TooltipInformation";
import { versaFieldHelperText } from "../../../constants/VersaHelperTexts";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { DNDFieldsEnum } from "../../../constants/Types";
import useScrollToTop from "../../hooks/useScrollToTop";

const Transportation = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [listName, setListName] = useState<string | null>(null);
  const [transportationType, setTransportationType] = useState<string | null>(
    null,
  );
  const [location, setLocation] = useState<string | null>(null);
  const [approxMiles, setApproxMiles] = useState<string | null>(null);
  const [approxTime, setApproxTime] = useState<string | null>(null);
  const [modeofTransport, setModeofTransport] = useState<"car" | "walk">("car");
  const [transportationInformation, setTransportationInformation] =
    useState<any>(null);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [loading, setLoading] = useState<boolean>(false);
  const { topRef, scrollToTop } = useScrollToTop();

  const addTransportation = () => {
    if (
      checkDuplicate(
        Array.isArray(transportationInformation)
          ? transportationInformation
          : [],
        DNDFieldsEnum.TRANSPORTATIONCODE,
        listName,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
    } else if (listName === null) {
      setAlertMessage("Enter the list name");
      setAlertType("error");
      setOpenAlert(true);
    } else {
      setTransportationInformation((prev: any) =>
        Array.isArray(prev)
          ? [
              ...prev,
              {
                id: prev.length + 1,
                code: listName,
                name: transportationType,
                location: location,
                approxMiles: approxMiles,
                approxTime: approxTime,
                modeofTransport: modeofTransport,
              },
            ]
          : [
              {
                id: 1,
                code: listName,
                name: transportationType,
                location: location,
                approxMiles: approxMiles,
                approxTime: approxTime,
                modeofTransport: modeofTransport,
              },
            ],
      );
    }
  };

  const editTransportation = (list: any) => {
    setListName(list.code);
    setTransportationType(list.name);
    setLocation(list.location);
    setApproxMiles(list.approxMiles);
    setApproxTime(list.approxTime);
    setModeofTransport(list.modeofTransport);
  };

  const deleteTransportation = (id: number) => {
    setTransportationInformation(deleteItem(transportationInformation, id));
  };

  useEffect(() => {
    data(transportationInformation);
  }, [transportationInformation]);

  useEffect(() => {
    fieldValues &&
      setTransportationInformation(
        Array.isArray(fieldValues)
          ? fieldValues
          : fieldValues
            ? [fieldValues]
            : null,
      );
  }, [fieldValues]);

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Typography variant="h6" component="div" sx={textStyle}>
            Transportation Information
            <Tooltip title={tooltipComponent(transportationTooltip.header)}>
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="column" gap="5px">
            <FormLabel component="legend">
              List Name
              <Tooltip title={tooltipComponent(transportationTooltip.listName)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              variant="outlined"
              placeholder="List Name"
              value={listName ?? ""}
              helperText={versaFieldHelperText.transportation.listName}
              onChange={(e: any) => {
                setListName(e.target.value);
              }}
            />
          </Stack>
          <Stack direction="column" gap="5px">
            <FormLabel component="legend">
              Transportation Type
              <Tooltip
                title={tooltipComponent(
                  transportationTooltip.transportationType,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              variant="outlined"
              placeholder="Transportation Type"
              value={transportationType ?? ""}
              helperText={
                versaFieldHelperText.transportation.transportationType
              }
              onChange={(e: any) => {
                setTransportationType(e.target.value);
              }}
            />
          </Stack>
          <Stack direction="column" gap="5px">
            <FormLabel component="legend">
              Location
              <Tooltip title={tooltipComponent(transportationTooltip.location)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              variant="outlined"
              placeholder="Location"
              value={location ?? ""}
              helperText={versaFieldHelperText.transportation.location}
              onChange={(e: any) => {
                setLocation(e.target.value);
              }}
            />
          </Stack>
          <Stack direction="column" gap="5px">
            <FormLabel component="legend">
              Approx Miles
              <Tooltip
                title={tooltipComponent(transportationTooltip.approxMiles)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              variant="outlined"
              placeholder="Approx Miles"
              type="number"
              value={approxMiles ?? ""}
              helperText={versaFieldHelperText.transportation.approxMiles}
              onChange={(e: any) => {
                setApproxMiles(e.target.value >= 0 ? e.target.value : null);
              }}
            />
          </Stack>
          <Stack
            className="form-two-col-row"
            direction="row"
            width="100%"
            gap="20px"
          >
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Approx Time
                <Tooltip
                  title={tooltipComponent(transportationTooltip.approxTime)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                variant="outlined"
                placeholder="Approx Time"
                type="number"
                value={approxTime ?? ""}
                helperText={versaFieldHelperText.transportation.approxTime}
                onChange={(e: any) => {
                  setApproxTime(e.target.value >= 0 ? e.target.value : null);
                }}
              />
            </Stack>
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Mode of Transport
                <Tooltip
                  title={tooltipComponent(
                    transportationTooltip.modeOfTransport,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <Select
                value={modeofTransport ?? ""}
                onChange={(e: any) => setModeofTransport(e.target.value)}
                sx={{ width: "100%" }}
              >
                <MenuItem value="car">Car</MenuItem>
                <MenuItem value="walk">Walk</MenuItem>
              </Select>
            </Stack>
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={addTransportation}
            >
              Add New Transportation Information
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">
              Transportation Information Available
            </Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {transportationInformation ? (
                <DraggableItemList
                  itemData={transportationInformation}
                  onOrderUpdate={(order) => setTransportationInformation(order)}
                  onEdit={(info) => {
                    editTransportation(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteTransportation(id)}
                  code={DNDFieldsEnum.TRANSPORTATIONCODE}
                  name={DNDFieldsEnum.TRANSPORTATIONNAME}
                />
              ) : (
                <Typography variant="body2">
                  No Transportation Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default Transportation;
